import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, X, CircleNotch } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi, getErrorMessage } from "@/lib/api";

function highlight(text, words) {
  if (!words.length) return text;
  const rx = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return text.split(rx).map((p, i) => (i % 2 ? <mark key={i} className="bg-amber-400/20 text-amber-200 rounded px-0.5">{p}</mark> : p));
}

function Item({ it, onAsk, onDismiss }) {
  const cur = Object.entries({ Solar: it.current.solar_configuration, Battery: it.current.battery_configuration, Hitch: it.current.hitch, Hubs: it.current.hub }).filter(([, v]) => v);
  return (
    <div className="rounded-xl border border-neutral-800 bg-[#121212] p-4 space-y-2" data-testid="review-item">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-sm text-[#73bf44]">{it.chassis_number}</span>
        <span className="text-xs text-neutral-500">{it.model}</span>
        <div className="ml-auto flex gap-1.5">
          <button onClick={() => onAsk(it)} data-testid="review-ask-ai" className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#73bf44]/15 text-[#73bf44] hover:bg-[#73bf44]/25"><Sparkle size={12} weight="fill" /> Write a rule</button>
          <button onClick={() => onDismiss(it)} data-testid="review-dismiss" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-neutral-700 text-neutral-400 hover:text-white"><X size={12} /> Dismiss</button>
        </div>
      </div>
      <p className="text-sm text-neutral-200 leading-relaxed">{highlight(it.notes, it.words)}</p>
      {cur.length > 0 && <div className="text-[0.7rem] text-neutral-500">Currently: {cur.map(([k, v]) => `${k} ${v}`).join(" · ")}</div>}
    </div>
  );
}

export function ReviewDialog({ open, onClose, onAsk, onCount }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    setItems(null);
    adminApi.get("/admin/rulebook/review", { timeout: 60000 }).then(({ data }) => { setItems(data); onCount(data.length); }).catch((e) => toast.error(getErrorMessage(e)));
  }, [open, onCount]);
  const dismiss = async (it) => {
    const before = items;
    const rest = items.filter((x) => x.chassis_number !== it.chassis_number);
    setItems(rest); onCount(rest.length);
    try {
      await adminApi.post("/admin/rulebook/review/dismiss", { chassis_number: it.chassis_number });
    } catch (e) { setItems(before); onCount(before.length); toast.error(getErrorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-heading text-white">Notes to review</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-400 -mt-1">These vans have Notes that mention solar, power, ATM, hubs or the hitch, but no rule reads them yet. Write a rule for the wording, or dismiss it if it doesn't matter.</p>
        {!items ? <div className="flex items-center gap-2 text-sm text-neutral-500 py-6"><CircleNotch size={16} className="animate-spin" /> Checking notes…</div> : items.length === 0 ? (
          <div className="text-sm text-[#73bf44] py-6" data-testid="review-empty">Nothing to review. Every note about power, ATM or hubs is handled by a rule.</div>
        ) : (
          <div className="space-y-2.5">{items.map((it) => <Item key={it.chassis_number} it={it} onAsk={(x) => { onAsk(x); onClose(); }} onDismiss={dismiss} />)}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
